/* TT-06 — Sticky add-to-cart bar (mobile PDP).
 *
 * The bar owns no cart state. Its button proxies to the real product form's
 * [data-add-to-cart], so the selected variant, quantity, Loop Subscriptions
 * selling plan, gift-card recipient, and any app-injected input are carried
 * by the real form. Everything here is display sync plus show/hide.
 *
 * Defensive throughout: if any expected element is missing, bail without
 * touching the page. A broken bar must never break the PDP.
 */
(function () {
  'use strict';

  function init(bar) {
    if (bar.dataset.stickyAtcReady === 'true') return;

    var section = bar.closest('.shopify-section') || document;
    var form = section.querySelector('[data-product-form]');
    if (!form) return;

    var realButton = form.querySelector('[data-add-to-cart]');
    if (!realButton) return;

    var barButton = bar.querySelector('[data-sticky-atc-button]');
    var barButtonText = bar.querySelector('[data-sticky-atc-button-text]');
    var barPrice = bar.querySelector('[data-sticky-atc-price]');
    var barVariant = bar.querySelector('[data-sticky-atc-variant]');
    if (!barButton || !barButtonText) return;

    var realPrice = section.querySelector('[data-product-price]');
    var variantPicker = section.querySelector('[data-product-variants]');
    var hasVariants = bar.dataset.hasVariants === 'true';

    bar.dataset.stickyAtcReady = 'true';

    // Portal the bar to <body>. Left inside the section it inherits an
    // ancestor stacking context, which put it *above* the cart drawer even
    // though the drawer is z-index 9999 and the bar is 900 — confirmed with
    // elementFromPoint over an open drawer. Raising the bar's own z-index
    // would not have helped; the comparison never reached the drawer. At body
    // level the z-index means what it says.
    var portalled = document.querySelector(
      '[data-sticky-atc][data-portalled="true"][data-section-id="' + bar.dataset.sectionId + '"]'
    );
    if (portalled && portalled !== bar) portalled.remove();
    if (bar.parentNode !== document.body) {
      document.body.appendChild(bar);
      bar.dataset.portalled = 'true';
    }

    /* ---- show / hide ------------------------------------------------- */

    // The drawer covers the viewport, so the bar is redundant while it is
    // open and would sit under the overlay looking half-dead.
    var drawerOpen = document.body.classList.contains('body-drawer-overflow');

    function setVisible(visible) {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) {
        bar.removeAttribute('inert');
      } else {
        bar.setAttribute('inert', '');
      }
    }

    // Deliberately a scroll handler rather than an IntersectionObserver.
    // IO only fires on *transitions*: a jump-scroll (anchor link, restored
    // scroll position, deep link) can take the buy box from below the fold to
    // above it without ever intersecting, so `isIntersecting` never changes
    // and no callback fires. Reading position directly is immune to that, and
    // also re-settles correctly when lazy-loaded images shift the layout.
    var ticking = false;

    function update() {
      ticking = false;
      // Show once the buy box has been scrolled fully past the top.
      setVisible(!drawerOpen && realButton.getBoundingClientRect().bottom < 0);
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('load', requestUpdate);

    // The theme emits these whether or not custom-events.js is loaded.
    document.addEventListener('flu:quick-cart:open', function () {
      drawerOpen = true;
      update();
    });
    document.addEventListener('flu:quick-cart:close', function () {
      drawerOpen = false;
      requestUpdate();
    });

    // Belt and braces: the theme toggles this class on <body> for any drawer,
    // including ones opened by routes the events above don't cover.
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        var open = document.body.classList.contains('body-drawer-overflow');
        if (open !== drawerOpen) {
          drawerOpen = open;
          update();
        }
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    update();

    /* ---- display sync ------------------------------------------------ */

    // Price. Normally this just mirrors the buy box. The exception is a
    // selected Subscribe & Save plan: Loop renders its discounted price inside
    // its own widget and never updates [data-product-price], so mirroring
    // alone would show $22.00 while the shopper is about to pay $18.70.
    // Shopify's own product JSON carries selling_plan_allocations, so the
    // subscription price is read from there rather than scraped out of app DOM.
    var productData = null;
    try {
      var json = document.getElementById('ProductJson-' + bar.dataset.sectionId);
      if (json) productData = JSON.parse(json.textContent);
    } catch (e) {
      productData = null;
    }

    function formatMoney(cents) {
      var fmt =
        (window.theme && window.theme.moneyFormat) ||
        (window.Shopify && window.Shopify.money_format) ||
        '${{amount}}';
      var value = cents / 100;
      function grouped(n, decimals, thousands, decimalMark) {
        var parts = n.toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimalMark);
      }
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
        switch (name) {
          case 'amount_no_decimals':
            return grouped(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return grouped(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return grouped(value, 0, '.', ',');
          default:
            return grouped(value, 2, ',', '.');
        }
      });
    }

    function subscriptionPrice() {
      if (!productData) return null;
      var planInput = form.querySelector('[name="selling_plan"]');
      var idInput = form.querySelector('[name="id"]');
      if (!planInput || !planInput.value || !idInput || !idInput.value) return null;

      var variant = null;
      for (var i = 0; i < productData.variants.length; i++) {
        if (String(productData.variants[i].id) === String(idInput.value)) {
          variant = productData.variants[i];
          break;
        }
      }
      if (!variant || !variant.selling_plan_allocations) return null;

      for (var j = 0; j < variant.selling_plan_allocations.length; j++) {
        var alloc = variant.selling_plan_allocations[j];
        if (String(alloc.selling_plan_id) === String(planInput.value)) {
          var cents = alloc.per_delivery_price != null ? alloc.per_delivery_price : alloc.price;
          return typeof cents === 'number' ? formatMoney(cents) : null;
        }
      }
      return null;
    }

    function syncPrice() {
      if (!barPrice) return;
      var sub = subscriptionPrice();
      if (sub) barPrice.textContent = sub;
      else if (realPrice) barPrice.textContent = realPrice.textContent.trim();
    }

    if (realPrice && barPrice && 'MutationObserver' in window) {
      new MutationObserver(syncPrice).observe(realPrice, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    // Loop sets the hidden selling_plan input as a *property*, which no
    // MutationObserver reports, and it does so asynchronously some time after
    // the click. Its purchase-option controls are also plain divs, so there is
    // no change event to wait on either. Re-check over a short window after any
    // interaction instead of guessing a single delay — a one-shot recompute
    // races Loop and intermittently leaves the one-time price on screen while
    // a subscription is selected.
    var settleTimers = [];
    function schedulePriceSync() {
      settleTimers.forEach(clearTimeout);
      settleTimers = [0, 120, 350, 700, 1200].map(function (delay) {
        return setTimeout(syncPrice, delay);
      });
    }
    form.addEventListener('click', schedulePriceSync, true);
    form.addEventListener('change', schedulePriceSync, true);

    // Mirror the real button's availability and label (sold out, unavailable).
    function syncButton() {
      var disabled =
        realButton.hasAttribute('disabled') ||
        realButton.getAttribute('aria-disabled') === 'true';

      barButton.disabled = disabled;
      barButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');

      var realText = realButton.querySelector('[data-add-to-cart-text]');
      if (realText) barButtonText.textContent = realText.textContent.trim();
    }

    if ('MutationObserver' in window) {
      new MutationObserver(syncButton).observe(realButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    document.addEventListener('flu:product:variant-change', function (evt) {
      var variant = evt && evt.detail && evt.detail.variant;
      if (barVariant && variant && variant.title) {
        barVariant.textContent = variant.title;
      }
      syncPrice();
      syncButton();
    });

    syncPrice();
    syncButton();

    /* ---- proxy ------------------------------------------------------- */

    function hasSelection() {
      if (!hasVariants) return true;
      var idInput = form.querySelector('[name="id"]');
      return !!(idInput && idInput.value);
    }

    barButton.addEventListener('click', function (event) {
      event.preventDefault();

      if (!hasSelection()) {
        if (variantPicker) {
          variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var firstControl = variantPicker.querySelector(
            'input:not([type="hidden"]), select, button, [tabindex]'
          );
          if (firstControl) firstControl.focus({ preventScroll: true });
        }
        return;
      }

      // Click rather than form.submit() so every handler Lorenza and Loop
      // bound to the real button runs exactly as it does from the buy box.
      realButton.click();
    });
  }

  function initAll() {
    document.querySelectorAll('[data-sticky-atc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', initAll);
})();
